/**
 * Bloque B: Ranking de opciones (1-4 por importancia)
 * 
 * @author Ana Lizcano
 */

import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { styled } from "@mui/material/styles";
import { 
  GridContainer,
  RankingCard,
  ScrollableArea,
  ProgressIndicator
} from '../../styles/commonStyles';
import { getBlockInstructions } from 'layouts/candidate-test/data/lazyQuestions';

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import { Select, MenuItem, FormControl } from "@mui/material";

// Select con colores por ranking (4=verde, 3=azul, 2=naranja, 1=rojo)
const StyledSelect = styled(Select)(({ theme, rankvalue }) => {
  const colors = {
    '4': '#81c784',
    '3': '#64b5f6', 
    '2': '#ffb74d',
    '1': '#f06292'
  };
  
  const selectedColor = colors[rankvalue] || '#e0e0e0';
  
  return {
    minWidth: '60px',
    height: '36px',
    borderRadius: '8px',
    fontSize: '13px',
    fontWeight: 'bold',
    '& .MuiSelect-select': {
      padding: theme.spacing(0.5),
      backgroundColor: rankvalue ? selectedColor : '#f5f5f5',
      color: rankvalue ? '#ffffff' : '#666666',
      border: `2px solid ${selectedColor}`,
      borderRadius: '8px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    '& .MuiOutlinedInput-notchedOutline': {
      border: 'none',
    },
    '& .MuiSelect-icon': {
      color: rankvalue ? '#ffffff' : '#666666',
    },
  };
});

// Badge circular con color según valor de ranking
const RankingBadge = styled(MDBox)(({ value }) => {
  const colors = {
    '4': '#81c784',
    '3': '#64b5f6',
    '2': '#ffb74d',
    '1': '#f06292'
  };
  
  return {
    width: '20px',
    height: '20px',
    borderRadius: '50%',
    backgroundColor: colors[value],
    color: '#ffffff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '11px',
    fontWeight: 'bold',
  };
});

const BlockB = ({ question, response, onResponseChange }) => {
  if (!question || question.type !== 'ranking') return null;

  const responses = response || {};
  
  const handleRankingChange = (optionIndex, value) => {
    const newResponses = { ...responses };
    
    Object.keys(newResponses).forEach(key => {
      if (newResponses[key] === value && key !== optionIndex.toString()) {
        delete newResponses[key];
      }
    });
    
    if (value === '') {
      delete newResponses[optionIndex];
    } else {
      newResponses[optionIndex] = parseInt(value);
    }
    
    onResponseChange(newResponses);
  };
  const completedOptions = useMemo(() => Object.keys(responses).length, [responses]);
  const isComplete = useMemo(() => completedOptions === 4, [completedOptions]);
  
  const getAvailableValues = useMemo(() => {
    return (currentOptionIndex) => {
      const usedValues = Object.keys(responses)
        .filter(key => key !== currentOptionIndex.toString())
        .map(key => responses[key]);
      
      return [1, 2, 3, 4].filter(value => !usedValues.includes(value));
    };
  }, [responses]);

  return (
    <MDBox sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      
      <MDBox sx={{ marginBottom: '20px', textAlign: 'center' }}>
        <MDTypography variant="h5" fontWeight="bold" color="dark" sx={{ fontSize: '1.4rem', marginBottom: '8px' }}>
          {question.question}
        </MDTypography>
      </MDBox>


      <ScrollableArea>
        {question.options.map((option, index) => {
          const currentValue = responses[index];
          const availableValues = getAvailableValues(index);
          
          return (
            <GridContainer key={index} columns={2} gap={1.5} sx={{ 
              marginBottom: '12px',
              gridTemplateColumns: '3fr 1fr' // Más espacio para la pregunta, menos para el select
            }}>
              <RankingCard rankvalue={currentValue?.toString()}>
                <MDTypography
                  variant="body1"
                  sx={{
                    fontSize: '14px',
                    fontWeight: currentValue ? 'bold' : 'medium',
                    lineHeight: 1.4,
                  }}
                >
                  {option.text}
                </MDTypography>
              </RankingCard>
              
              <FormControl>
                <StyledSelect
                  value={currentValue || ''}
                  onChange={(e) => handleRankingChange(index, e.target.value)}
                  displayEmpty
                  rankvalue={currentValue?.toString()}
                >
                  <MenuItem value="">
                    <em>Seleccionar</em>
                  </MenuItem>
                  {currentValue && (
                    <MenuItem value={currentValue}>
                      {currentValue}
                    </MenuItem>
                  )}
                  {availableValues.map((value) => (
                    <MenuItem key={value} value={value}>
                      {value}
                    </MenuItem>
                  ))}
                </StyledSelect>
              </FormControl>
            </GridContainer>
          );
        })}
      </ScrollableArea>

      

      <ProgressIndicator variant={isComplete ? 'success' : 'default'}>
        <MDTypography variant="body2" color={isComplete ? "success" : "text"}>
          <strong>Progreso:</strong> {completedOptions} de 4 opciones completadas
        </MDTypography>
        {isComplete && (
          <MDTypography variant="caption" color="success" sx={{ mt: 0.5 }}>
            ✓ Todas las opciones han sido clasificadas
          </MDTypography>
        )}
      </ProgressIndicator>
    </MDBox>
  );
};

BlockB.propTypes = {
  question: PropTypes.shape({
    question: PropTypes.string.isRequired,
    type: PropTypes.string.isRequired,
    instruction: PropTypes.string.isRequired,
    options: PropTypes.arrayOf(PropTypes.string).isRequired
  }).isRequired,
  response: PropTypes.object,
  onResponseChange: PropTypes.func.isRequired
};

export default BlockB;